import { numberLocative } from './format.js';

/**
 * The license's days on the screens (D-123): whole days in Istanbul, written as days and never with a time:
 * "1 Ekim 2026", "2–30 Eylül 2026", "1 Ekim 2026 – 30 Eylül 2027", and in Turkish with the ending the
 * sentence needs ("30 Eylül'de", "15 Ekim'den", "1 Ekim 2027'de"). English writes the day first as well.
 */
const noon = (day: string) => new Date(`${day}T12:00:00Z`);
const tag = (locale: string) => (locale.startsWith('tr') ? 'tr' : 'en-GB');
const format = (locale: string, year: boolean) =>
  new Intl.DateTimeFormat(tag(locale), { day: 'numeric', month: 'long', ...(year ? { year: 'numeric' } : {}), timeZone: 'UTC' });

/** YYYY-MM-DD plus n days */
export function addDays(day: string, n: number): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** whole days from one day to another (YYYY-MM-DD) */
export function daysBetween(from: string, to: string): number {
  return Math.round((noon(to).getTime() - noon(from).getTime()) / 86_400_000);
}

export function licenseDay(day: string, locale: string, year = true): string {
  return format(locale, year).format(noon(day));
}

/** days of one month close up ("1–30 Eylül"), as with the year; across months plain spaces round the dash (not ICU's thin ones) */
export function licenseRange(from: string, until: string, locale: string, year = true): string {
  return format(locale, year)
    .formatRange(noon(from), noon(until))
    .replace(/\u2009/gu, ' ')
    .replace(/(^|\s)(\d{1,2})\s*–\s*(\d)/u, '$1$2–$3');
}

/** Ocak'ta, Şubat'ta … Eylül'de, Ekim'de, Kasım'da, Aralık'ta */
const MONTH_LOCATIVE = ['ta', 'ta', 'ta', 'da', 'ta', 'da', 'da', 'ta', 'de', 'de', 'da', 'ta'];

const ending = (day: string, year: boolean) => (year ? numberLocative(Number(day.slice(0, 4))) : (MONTH_LOCATIVE[Number(day.slice(5, 7)) - 1] ?? 'de'));

/** "30 Eylül'de" (English: "30 September"; the sentence says "on") */
export function licenseDayAt(day: string, locale: string, year = false): string {
  const text = licenseDay(day, locale, year);
  return locale.startsWith('tr') ? `${text}'${ending(day, year)}` : text;
}

/** "15 Ekim'den" (English: "15 October"; the sentence says "from") */
export function licenseDayFrom(day: string, locale: string, year = false): string {
  const text = licenseDay(day, locale, year);
  return locale.startsWith('tr') ? `${text}'${ending(day, year)}n` : text;
}

/** Ocak'a, Şubat'a … Eylül'e, Ekim'e, Kasım'a, Aralık'a */
const MONTH_DATIVE = ['a', 'a', 'a', 'a', 'a', 'a', 'a', 'a', 'e', 'e', 'a', 'a'];

/** "30 Kasım'a" without the year (English: "30 November"; the sentence says "until") */
export function licenseDayTo(day: string, locale: string): string {
  const text = licenseDay(day, locale, false);
  return locale.startsWith('tr') ? `${text}'${MONTH_DATIVE[Number(day.slice(5, 7)) - 1] ?? 'e'}` : text;
}
